import { Link } from '@tanstack/react-router';
import { CreditCard, Info, KeyRound, Loader2, UserMinus } from 'lucide-react';
import { useState } from 'react';

import {
  type FamilyCommunicationLevel,
  type FamilyContactWithParent,
  useGetFamilyHub,
  useRemoveFamilyContact,
  useSetFamilyBillingParent,
  useUpdateFamilyContact,
} from '~entities/family';
import { useCurrentStudio } from '~entities/studios/model/studio-store';
import { useCurrentUserRole } from '~entities/users/hooks/use-current-user-role';

import { LinkGuardianSection } from '~features/contacts/family/link-guardian-section';
import { ResetPassword } from '~features/students/ui/reset-password';

import { formatDateSafe } from '~shared/helpers/datetime';

import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '~shared/shadcn/ui/alert-dialog';
import { Button } from '~shared/shadcn/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '~shared/shadcn/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '~shared/shadcn/ui/table';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '~shared/shadcn/ui/tooltip';

const joinParentName = (contact: FamilyContactWithParent): string => {
  const first = contact.parent?.firstName?.trim() ?? '';
  const last = contact.parent?.lastName?.trim() ?? '';
  return `${first} ${last}`.trim();
};

const guardianDisplayName = (contact: FamilyContactWithParent): string => {
  const joined = joinParentName(contact);
  if (joined) return joined;
  const named = contact.parent?.name?.trim();
  if (named) return named;
  return contact.parentId;
};

const LabelWithTip = ({
  label,
  tip,
}: {
  label: string;
  tip: string;
}) => (
  <span className='inline-flex items-center gap-1'>
    {label}
    <TooltipProvider delayDuration={200}>
      <Tooltip>
        <TooltipTrigger asChild>
          <button
            type='button'
            className='text-muted-foreground hover:text-foreground'
            aria-label={`${label} help`}
          >
            <Info className='size-3.5' />
          </button>
        </TooltipTrigger>
        <TooltipContent className='max-w-xs text-left'>
          <p>{tip}</p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  </span>
);

/**
 * V2 family-hub guardians table (ENR-4054).
 * Portal Access toggle is hidden until Off can block Parent Portal login
 * (today it only hides students; Brad: scrap until login-block ships).
 */
export const FamilyHubGuardians = ({ familyId }: { familyId: string }) => {
  const studio = useCurrentStudio();
  const locationId = (studio?.locationId ?? '').trim();
  const { hasSuperAdminAccess } = useCurrentUserRole();
  const { data, isLoading, isError } = useGetFamilyHub(familyId);
  const setBilling = useSetFamilyBillingParent();
  const updateContact = useUpdateFamilyContact();
  const removeContact = useRemoveFamilyContact();

  const [pendingRemove, setPendingRemove] =
    useState<FamilyContactWithParent | null>(null);
  const [pendingBilling, setPendingBilling] = useState<{
    parentId: string;
    displayName: string;
  } | null>(null);
  const [resetEmail, setResetEmail] = useState<string | null>(null);

  if (isLoading) {
    return (
      <div className='flex items-center gap-2 text-sm text-muted-foreground'>
        <Loader2 className='size-4 animate-spin' />
        Loading guardians…
      </div>
    );
  }

  if (isError || !data) {
    return (
      <p className='text-sm text-destructive'>Could not load guardians.</p>
    );
  }

  const contacts = data.contacts;
  const primaryStudentId = data.students[0]?._id;
  const studentParentIds = new Set(
    data.students.map((s) => s.parentId).filter(Boolean),
  );

  return (
    <>
      <div className='mb-4 space-y-1'>
        <h2 className='text-2xl font-semibold'>Family Guardians</h2>
        <p className='text-sm text-muted-foreground'>
          Manage logins, billing, and communication for this household.
        </p>
      </div>

      <div className='overflow-x-auto rounded-md border'>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Name</TableHead>
              <TableHead>Relationship</TableHead>
              <TableHead>Phone</TableHead>
              <TableHead>Email</TableHead>
              <TableHead>Billing</TableHead>
              <TableHead>Login</TableHead>
              <TableHead>
                <LabelWithTip
                  label='Communication'
                  tip='Full: receives family emails and announcements. Limited: reduced family communication (studio policy).'
                />
              </TableHead>
              <TableHead>Last login</TableHead>
              <TableHead className='text-right'>Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {contacts.map((contact) => {
              const email = contact.parent?.email?.trim() ?? '';
              const phone = contact.parent?.primaryPhone?.trim() ?? '';
              const canRemove =
                !contact.isBillingContact &&
                !studentParentIds.has(contact.parentId);

              return (
                <TableRow key={contact.parentId}>
                  <TableCell className='font-medium'>
                    {guardianDisplayName(contact)}
                  </TableCell>
                  <TableCell>
                    {contact.relationship ?? 'Guardian'}
                  </TableCell>
                  <TableCell>{phone || '—'}</TableCell>
                  <TableCell className='max-w-[12rem] truncate'>
                    {email || '—'}
                  </TableCell>
                  <TableCell>
                    {contact.isBillingContact ? (
                      <span className='rounded-md bg-primary/10 px-2 py-0.5 text-xs font-medium text-primary'>
                        Billing
                      </span>
                    ) : (
                      <Button
                        size='sm'
                        variant='outline'
                        disabled={setBilling.isPending}
                        onClick={() => {
                          setPendingBilling({
                            parentId: contact.parentId,
                            displayName: guardianDisplayName(contact),
                          });
                        }}
                      >
                        Set billing
                      </Button>
                    )}
                  </TableCell>
                  <TableCell>
                    {contact.hasLogin ? 'Yes' : 'No password'}
                  </TableCell>
                  <TableCell>
                    <Select
                      value={contact.communicationLevel}
                      disabled={updateContact.isPending}
                      onValueChange={(value) => {
                        updateContact.mutate({
                          familyId,
                          parentId: contact.parentId,
                          communicationLevel: value as FamilyCommunicationLevel,
                        });
                      }}
                    >
                      <SelectTrigger className='w-[7rem]'>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value='Full'>Full</SelectItem>
                        <SelectItem value='Limited'>Limited</SelectItem>
                      </SelectContent>
                    </Select>
                  </TableCell>
                  <TableCell className='whitespace-nowrap text-sm text-muted-foreground'>
                    {formatDateSafe(
                      contact.lastLoginAt,
                      'MMM d, yyyy h:mm a',
                      '—',
                    )}
                  </TableCell>
                  <TableCell className='text-right'>
                    <div className='flex flex-wrap justify-end gap-1'>
                      {hasSuperAdminAccess && email ? (
                        <Button
                          size='sm'
                          variant='ghost'
                          title='Reset password'
                          aria-label='Reset password'
                          onClick={() => {
                            setResetEmail(email);
                          }}
                        >
                          <KeyRound className='size-4' />
                        </Button>
                      ) : null}
                      <Button
                        size='sm'
                        variant='ghost'
                        asChild
                        title='Payment Details'
                      >
                        <Link
                          to='/parents/$parentId/saved-payment-methods'
                          params={{ parentId: contact.parentId }}
                        >
                          <CreditCard className='size-4' />
                        </Link>
                      </Button>
                      <Button
                        size='sm'
                        variant='ghost'
                        className={
                          canRemove
                            ? 'text-muted-foreground hover:text-destructive'
                            : 'text-muted-foreground/40'
                        }
                        disabled={!canRemove || removeContact.isPending}
                        title={
                          canRemove
                            ? 'Remove from family'
                            : 'Cannot remove billing or primary parent'
                        }
                        onClick={() => {
                          if (!canRemove) return;
                          setPendingRemove(contact);
                        }}
                      >
                        <UserMinus className='size-4' />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>

      <div className='mt-6 max-w-2xl'>
        {locationId && primaryStudentId ? (
          <LinkGuardianSection
            familyId={familyId}
            studentId={primaryStudentId}
            locationId={locationId}
            linkedParentIds={contacts.map((c) => c.parentId)}
          />
        ) : (
          <p className='text-sm text-muted-foreground'>
            Link a student to this family before adding more guardians.
          </p>
        )}
      </div>

      <ResetPassword
        email={resetEmail ?? ''}
        open={resetEmail !== null}
        setOpen={(open) => {
          if (!open) setResetEmail(null);
        }}
      />

      <AlertDialog
        open={pendingBilling !== null}
        onOpenChange={(open) => {
          if (!open && !setBilling.isPending) setPendingBilling(null);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              Set {pendingBilling?.displayName ?? 'this guardian'} as billing?
            </AlertDialogTitle>
            <AlertDialogDescription>
              Bulk charges that use the family billing parent will charge this
              guardian by default.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={setBilling.isPending}>
              Cancel
            </AlertDialogCancel>
            <Button
              disabled={setBilling.isPending || !pendingBilling}
              onClick={() => {
                if (!pendingBilling) return;
                setBilling.mutate(
                  {
                    familyId,
                    parentId: pendingBilling.parentId,
                  },
                  { onSuccess: () => setPendingBilling(null) },
                );
              }}
            >
              Set as billing
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog
        open={pendingRemove !== null}
        onOpenChange={(open) => {
          if (!open && !removeContact.isPending) setPendingRemove(null);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              Remove{' '}
              {pendingRemove ? guardianDisplayName(pendingRemove) : 'guardian'}?
            </AlertDialogTitle>
            <AlertDialogDescription>
              They are unlinked from this household. Their Parent login and any
              students they own stay intact.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={removeContact.isPending}>
              Keep guardian
            </AlertDialogCancel>
            <Button
              variant='destructive'
              disabled={removeContact.isPending || !pendingRemove}
              onClick={() => {
                if (!pendingRemove) return;
                removeContact.mutate(
                  {
                    familyId,
                    parentId: pendingRemove.parentId,
                  },
                  { onSuccess: () => setPendingRemove(null) },
                );
              }}
            >
              Remove guardian
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};
